import {ANALYTICS} from "@/store/analytics/helpers.js";
import {ACTION_LOAD_LAST_PARKING_DETECTION_EVENT} from "@/store/analytics/parkingDetections/index.js";

const DEFAULT_PARKING_REFRESH_INTERVAL_MS = 5000;

/**
 * Логика актуального состояния parking_detection для боковой панели плеера.
 */
export const parkingDetectionEventsMixin = {
  props: {
    /**
     * Текущая точка просмотра архива, на которую запрашивается состояние парковки.
     */
    parkingStateDate: {
      type: Date,
      default: null,
    },
    /**
     * Режим отображения актуального состояния парковки в live.
     */
    liveParkingMode: {
      type: Boolean,
      default: false,
    },
    /**
     * Интервал обновления актуального состояния парковочных мест.
     */
    parkingRefreshIntervalMs: {
      type: Number,
      default: DEFAULT_PARKING_REFRESH_INTERVAL_MS,
    },
  },
  data() {
    return {
      parkingRequestCounter: 0,
      parkingRefreshEnabled: false,
      parkingRefreshTimeoutId: null,
      parkingStateRefreshTimeoutId: null,
      isParkingStateLoading: false,
      lastParkingMessage: null,
      lastParkingStateRequestAt: 0,
    };
  },
  watch: {
    /**
     * Отслеживание точки просмотра архива для актуализации состояния парковки.
     *
     * @param {Date} newValue
     * @param {Date} oldValue
     */
    parkingStateDate(newValue, oldValue) {
      if (!this.liveParkingMode && +newValue !== +oldValue) {
        this.scheduleParkingStateRefresh();
      }
    },
    liveParkingMode(newValue) {
      if (newValue) {
        this.reloadLiveParkingState();
      } else {
        this.stopParkingRefresh();
        this.loadMessages();
      }
    },
    "cameraInfo.number"(newValue, oldValue) {
      if (newValue === oldValue || !this.liveParkingMode) {
        return;
      }
      this.reloadLiveParkingState();
    },
  },
  beforeDestroy() {
    this.stopParkingRefresh();
    clearTimeout(this.parkingStateRefreshTimeoutId);
  },
  methods: {
    /**
     * Проверяет, включена ли на камере аналитика парковочных мест.
     *
     * @return {Boolean}
     */
    hasParkingDetection() {
      return (
        this.cameraInfo &&
        Array.isArray(this.cameraInfo.analytics) &&
        this.cameraInfo.analytics.includes(ANALYTICS.parking_detection)
      );
    },
    /**
     * Возвращает дату, на которую нужно получить последнее состояние парковки.
     *
     * @return {Date}
     */
    getParkingStateDateObject() {
      if (this.liveParkingMode) {
        return new Date();
      }
      if (this.parkingStateDate) {
        return new Date(this.parkingStateDate);
      }
      if (this.archiveFrom && this.archiveTo) {
        return new Date((+this.archiveFrom + +this.archiveTo) / 2);
      }
      return new Date();
    },
    /**
     * Возвращает дату, на которую нужно получить последнее состояние парковки.
     *
     * @return {Date|String}
     */
    getParkingStateDate() {
      const date = this.getParkingStateDateObject();

      if (this.cameraInfo.timezone) {
        return moment.tz(date, this.cameraInfo.timezone).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
      }

      return date;
    },
    /**
     * Эмитит состояние парковочных мест в плеер.
     *
     * @param {ParkingDetectionMessage} parkingEvent
     */
    emitParkingSpots(parkingEvent) {
      if (parkingEvent && parkingEvent.parkingSpots) {
        this.$emit("parking-spots-updated", parkingEvent.parkingSpots);
      }
    },
    /**
     * Запрашивает последнее состояние парковочных мест и эмитит spots наверх.
     *
     * @param {Boolean} showLoader
     * @param {Boolean} replaceMessages
     * @param {Boolean} emitSpots
     * @return {Promise<ParkingDetectionMessage>}
     */
    async loadLastParkingEvent({showLoader = false, replaceMessages = false, emitSpots = true} = {}) {
      if (!this.hasParkingDetection()) {
        this.isParkingStateLoading = false;
        return null;
      }

      const requestNumber = ++this.parkingRequestCounter;
      this.lastParkingStateRequestAt = Date.now();
      this.isParkingStateLoading = true;
      if (showLoader) {
        this.isLoading = true;
      }

      try {
        const lastParkingEvent = await this.$store.dispatch(
          `analytics/parkingDetections/${ACTION_LOAD_LAST_PARKING_DETECTION_EVENT}`,
          {
            cameraNumber: this.cameraInfo.number,
            date: this.getParkingStateDate(),
          }
        );
        if (requestNumber !== this.parkingRequestCounter || !lastParkingEvent) {
          return null;
        }

        lastParkingEvent.cameraInfo = this.cameraInfo;
        this.lastParkingMessage = lastParkingEvent;
        if (emitSpots) {
          this.emitParkingSpots(lastParkingEvent);
        }
        if (replaceMessages) {
          this.messages = [lastParkingEvent];
        }

        return lastParkingEvent;
      } catch (e) {
        console.error("Failed to load last parking detection event", e);
        return null;
      } finally {
        if (requestNumber === this.parkingRequestCounter) {
          this.isParkingStateLoading = false;
        }
        if (showLoader && requestNumber === this.parkingRequestCounter) {
          this.isLoading = false;
        }
      }
    },
    /**
     * Первичная загрузка и запуск периодического обновления parking_detection в live.
     */
    reloadLiveParkingState() {
      this.messagesRequestCounter += 1;
      this.messages = [];
      this.startParkingRefresh();
      this.loadLastParkingEvent({showLoader: true, replaceMessages: true});
    },
    /**
     * Запускает обновление последнего состояния парковки без наложения запросов.
     */
    startParkingRefresh() {
      this.stopParkingRefresh();
      if (!this.liveParkingMode || this.parkingRefreshIntervalMs <= 0) {
        return;
      }
      this.parkingRefreshEnabled = true;
      this.parkingRefreshTimeoutId = setTimeout(
        () => this.refreshLastParkingEvent(),
        this.parkingRefreshIntervalMs
      );
    },
    /**
     * Останавливает периодическое обновление parking_detection.
     */
    stopParkingRefresh() {
      this.parkingRefreshEnabled = false;
      clearTimeout(this.parkingRefreshTimeoutId);
      this.parkingRefreshTimeoutId = null;
      this.isParkingStateLoading = false;
      this.parkingRequestCounter += 1;
    },
    /**
     * Обновляет последнее состояние парковки и планирует следующий запрос.
     */
    async refreshLastParkingEvent() {
      if (!this.parkingRefreshEnabled) {
        return;
      }
      await this.loadLastParkingEvent({replaceMessages: true});
      if (this.parkingRefreshEnabled) {
        this.parkingRefreshTimeoutId = setTimeout(
          () => this.refreshLastParkingEvent(),
          this.parkingRefreshIntervalMs
        );
      }
    },
    /**
     * Откладывает тихое обновление состояния парковки без спиннера и очистки списка событий.
     */
    scheduleParkingStateRefresh() {
      clearTimeout(this.parkingStateRefreshTimeoutId);
      if (!this.hasParkingDetection()) {
        this.isParkingStateLoading = false;
        return;
      }
      if (this.messages.length === 0) {
        this.isParkingStateLoading = true;
      }
      const elapsedSinceLastRequest = Date.now() - this.lastParkingStateRequestAt,
            refreshDelay = Math.max(this.parkingRefreshIntervalMs - elapsedSinceLastRequest, 0);
      this.parkingStateRefreshTimeoutId = setTimeout(
        () => this.refreshParkingStateInMessages(),
        refreshDelay
      );
    },
    /**
     * Тихо обновляет только парковочное сообщение в боковой панели.
     */
    async refreshParkingStateInMessages() {
      clearTimeout(this.parkingStateRefreshTimeoutId);
      const parkingMessage = await this.loadLastParkingEvent({emitSpots: false});
      if (!parkingMessage) {
        return;
      }

      parkingMessage.cameraInfo = this.cameraInfo;
      this.emitParkingSpots(parkingMessage);

      const otherMessages = this.messages.filter(
        (message) => message.analyticType !== ANALYTICS.parking_detection
      );
      this.messages = [...otherMessages, parkingMessage].sort(
        (a, b) => +b.date - +a.date
      );
    },
  },
};
